import React, { useState, useEffect } from 'react';
import { Mail, ShieldCheck, ArrowRight, LogOut, Loader2, Edit3, CheckCircle, User } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { v4 as uuidv4 } from 'uuid';

interface EmailVerificationGateProps {
  type: 'admin' | 'manager';
  token: string;
  onVerified: () => void;
}

export function EmailVerificationGate({ type, token, onVerified }: EmailVerificationGateProps) {
  const [email, setEmail] = useState('');
  const [isEmailPredefined, setIsEmailPredefined] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [verificationInput, setVerificationInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [targetName, setTargetName] = useState('');
  const [recordId, setRecordId] = useState('');
  const [fullName, setFullName] = useState('');
  const [orgId, setOrgId] = useState('');
  const [categoryId, setCategoryId] = useState('');

  useEffect(() => {
    async function checkToken() {
      try {
        if (type === 'admin') {
          const { data, error: dbError } = await supabase
            .from('organization_admins')
            .select('id, email, email_confirmed, full_name, org_id, organizations(name)')
            .eq('token', token)
            .is('deleted_at', null)
            .maybeSingle();

          if (dbError) throw dbError;
          if (data) {
            setRecordId(data.id);
            setOrgId(data.org_id || '');
            setTargetName((data as any).organizations?.name || 'Organisation');
            if (data.email) {
              setEmail(data.email);
              setIsEmailPredefined(true);
              const { data: teamData } = await supabase
                .from('staff')
                .select('name')
                .eq('email', data.email.toLowerCase().trim())
                .is('deleted_at', null)
                .maybeSingle();
              if (teamData?.name) {
                setFullName(teamData.name);
              }
              // Also prefill from admin record if team record has no name
              if (!teamData?.name && data.full_name) {
                setFullName(data.full_name);
              }
            }
          } else {
            setError("Lien administrateur invalide ou expiré.");
          }
        } else {
          const { data, error: dbError } = await supabase
            .from('category_managers')
            .select('id, email, email_confirmed, full_name, category_id, categories(name, org_id)')
            .eq('token', token)
            .is('deleted_at', null)
            .maybeSingle();

          if (dbError) throw dbError;
          if (data) {
            setRecordId(data.id);
            setCategoryId(data.category_id || '');
            setOrgId((data as any).categories?.org_id || '');
            setTargetName((data as any).categories?.name || 'Catégorie');
            if (data.email) {
              setEmail(data.email);
              setIsEmailPredefined(true);
              const { data: teamData } = await supabase
                .from('staff')
                .select('name')
                .eq('email', data.email.toLowerCase().trim())
                .is('deleted_at', null)
                .maybeSingle();
              if (teamData?.name) {
                setFullName(teamData.name);
              }
              if (!teamData?.name && data.full_name) {
                setFullName(data.full_name);
              }
            }
          } else {
            setError("Lien manager invalide ou expiré.");
          }
        }
      } catch (err: any) {
        console.error("Error loading token data:", err);
        setError("Erreur de chargement des informations d'accès.");
      } finally {
        setLoading(false);
      }
    }
    checkToken();
  }, [type, token]);

  const handleLogout = async () => {
    localStorage.removeItem('wfm_manager_token');
    localStorage.removeItem('wfm_staff_token');
    localStorage.removeItem('wfm_org_token');
    localStorage.removeItem('wfm_current_org_id');
    localStorage.removeItem('wfm_device_id');
    window.location.href = '/';
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setError("Veuillez saisir une adresse email valide.");
      return;
    }

    setActionLoading(true);
    setError('');

    const cleanEmail = email.toLowerCase().trim();

    try {
      // 1. Generate 6-digit OTP
      const code = Math.floor(100000 + Math.random() * 900000).toString();
      console.log(`🔑 [DEBUG OTP] Code de validation pour ${cleanEmail} : ${code}`);
      
      // 2. Save email to the token record first (if modified or not set yet)
      const table = type === 'admin' ? 'organization_admins' : 'category_managers';
      const { error: updateError } = await supabase
        .from(table)
        .update({ email: cleanEmail })
        .eq('id', recordId);

      if (updateError) throw updateError;

      // Try to find if a team profile with this email already exists to prefill the name
      const { data: teamData } = await supabase
        .from('staff')
        .select('name')
        .eq('email', cleanEmail)
        .is('deleted_at', null)
        .maybeSingle();

      if (teamData?.name) {
        setFullName(teamData.name);
      }

      // 3. Write OTP to database (this will fire the trigger and send the email!)
      const { error: otpError } = await supabase
        .from('login_otps')
        .insert({
          email: cleanEmail,
          code: code,
          expires_at: new Date(Date.now() + 15 * 60 * 1000).toISOString()
        });

      if (otpError) throw otpError;

      setCodeSent(true);
      setIsEditing(false);
    } catch (err: any) {
      console.error("Error sending OTP:", err);
      setError("Impossible d'envoyer le code de validation. Veuillez réessayer.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim()) {
      setError("Veuillez saisir votre nom et prénom.");
      return;
    }
    if (verificationInput.length !== 6) {
      setError("Le code doit comporter 6 chiffres.");
      return;
    }

    setActionLoading(true);
    setError('');

    try {
      const rpcName = type === 'admin' ? 'confirm_admin_email' : 'confirm_manager_email';
      const { error: dbError } = await supabase.rpc(rpcName, {
        p_token: token,
        p_code: verificationInput,
        p_email: email.toLowerCase().trim()
      });

      if (dbError) throw dbError;

      // Save full_name to the admin/manager record
      const adminTable = type === 'admin' ? 'organization_admins' : 'category_managers';
      supabase.from(adminTable).update({ full_name: fullName.trim() }).eq('id', recordId).then(({ error: nameErr }) => {
        if (nameErr) console.error('Error saving full_name:', nameErr);
      });

      const cleanEmail = email.toLowerCase().trim();

      // Create or update linked team profile
      const { data: existingTeam } = await supabase
        .from('staff')
        .select('id')
        .eq('email', cleanEmail)
        .is('deleted_at', null)
        .maybeSingle();

      if (!existingTeam) {
        const teamId = uuidv4();
        const teamColors = ['#6366f1', '#ec4899', '#14b8a6', '#f59e0b', '#ef4444', '#8b5cf6', '#10b981', '#3b82f6'];
        const randomColor = teamColors[Math.floor(Math.random() * teamColors.length)];
        const teamToken = uuidv4();

        const { error: insertErr } = await supabase
          .from('staff')
          .insert({
            id: teamId,
            name: fullName.trim(),
            email: cleanEmail,
            email_confirmed: true,
            status: 'approved',
            role: 'staff',
            roles: [],
            color: randomColor,
            org_id: orgId || null,
            category_id: categoryId || null,
            staff_token: teamToken,
          });

        if (insertErr) {
          console.error("Error creating team record:", insertErr);
        }
      } else {
        const { error: updateErr } = await supabase
          .from('staff')
          .update({
            email_confirmed: true,
            name: fullName.trim(),
            category_id: categoryId || null,
            org_id: orgId || null
          })
          .eq('id', existingTeam.id);

        if (updateErr) {
          console.error("Error updating existing team record:", updateErr);
        }
      }

      // Persist the confirmed email to local storage
      localStorage.setItem('wfm_user_email', cleanEmail);

      setSuccess(true);
      setTimeout(() => {
        onVerified();
      }, 1500);
    } catch (err: any) {
      console.error("Error confirming email:", err);
      setError(err.message || "Une erreur est survenue lors de la validation. Veuillez réessayer.");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[100dvh] w-full items-center justify-center bg-[#0A0B0E] text-white">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="flex min-h-[100dvh] w-full items-center justify-center bg-[#0A0B0E] p-4 text-white font-sans relative selection:bg-indigo-500/30">
      {/* Background ambient glows */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(99,102,241,0.08),transparent_45%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(99,102,241,0.05),transparent_45%)] pointer-events-none" />

      <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-[#2D3139] bg-[#111318]/90 p-8 shadow-2xl backdrop-blur-xl transition-all duration-300 hover:border-slate-800">
        
        {/* Top border glow */}
        <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-indigo-500 to-transparent" />

        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 shadow-lg shadow-indigo-500/5">
            {codeSent ? <ShieldCheck className="h-7 w-7 animate-pulse" /> : <Mail className="h-7 w-7" />}
          </div>
          <h1 className="text-xl font-bold tracking-tight text-slate-100">
            {type === 'admin' ? "Validation Compte Administrateur" : "Validation Compte Manager"}
          </h1>
          <p className="mt-2 text-xs text-slate-400">
            {type === 'admin' ? "Organisation" : "Catégorie"} : <span className="font-semibold text-indigo-400">{targetName}</span>
          </p>
        </div>

        {error && (
          <div className="mb-6 rounded-lg border border-red-500/20 bg-red-500/5 p-4 text-center text-xs font-medium text-red-400 backdrop-blur-sm animate-shake">
            {error}
          </div>
        )}

        {success ? (
          <div className="space-y-4 text-center py-6">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              <CheckCircle className="h-6 w-6" />
            </div>
            <h2 className="text-base font-bold text-emerald-400">Email validé avec succès !</h2>
            <p className="text-xs text-slate-400">Accès sécurisé activé. Chargement...</p>
          </div>
        ) : !codeSent ? (
          <form onSubmit={handleSendCode} className="space-y-5">
            {isEmailPredefined && !isEditing ? (
              <div className="bg-[#0A0B0E] border border-[#2D3139] p-4 rounded-xl space-y-3">
                <p className="text-xs text-slate-400 leading-relaxed text-center">
                  L'adresse email suivante a été configurée par le superadmin. Veuillez la valider pour activer votre accès.
                </p>
                <div className="text-center font-bold text-sm text-slate-200 py-1 bg-[#14161C] border border-[#2D3139]/60 rounded-lg">
                  {email}
                </div>
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => setIsEditing(true)}
                    className="text-[10px] text-slate-400 hover:text-indigo-400 transition-colors flex items-center gap-1 font-bold uppercase tracking-wider"
                  >
                    <Edit3 className="w-3 h-3" /> Modifier l'adresse email
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                  Saisir votre adresse email professionnelle
                </label>
                <div className="relative">
                  <input
                    type="email"
                    required
                    placeholder="nom@exemple.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-xl border border-[#2D3139] bg-[#0A0B0E] py-3.5 pl-11 pr-4 text-sm font-medium text-slate-200 placeholder-slate-600 transition-all focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/15"
                  />
                  <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                </div>
                {isEmailPredefined && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditing(false);
                        // Reset to original predefined email
                        supabase
                          .from(type === 'admin' ? 'organization_admins' : 'category_managers')
                          .select('email')
                          .eq('token', token)
                          .maybeSingle()
                          .then(({ data }) => {
                            if (data?.email) setEmail(data.email);
                          });
                      }}
                      className="text-[9px] text-slate-500 hover:text-slate-300 font-bold uppercase"
                    >
                      Annuler la modification
                    </button>
                  </div>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={actionLoading}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 py-3.5 text-xs font-bold uppercase tracking-wider text-white transition-all hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 disabled:opacity-50 active:scale-[0.98]"
            >
              {actionLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Envoyer le Code de Validation
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyCode} className="space-y-5">
            <p className="text-xs leading-relaxed text-slate-400 text-center">
              Nous avons envoyé un code de confirmation à 6 chiffres à l'adresse <strong className="text-indigo-400">{email}</strong>. Saisissez-le ci-dessous pour valider votre compte.
            </p>

            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                Votre Nom et Prénom
              </label>
              <div className="relative">
                <input
                  type="text"
                  required
                  placeholder="Nom Prénom"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full rounded-xl border border-[#2D3139] bg-[#0A0B0E] py-3.5 pl-11 pr-4 text-sm font-medium text-slate-200 placeholder-slate-600 transition-all focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/15"
                />
                <User className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block text-center">
                Code de Confirmation (6 chiffres)
              </label>
              <input
                type="text"
                required
                maxLength={6}
                pattern="[0-9]{6}"
                placeholder="000000"
                value={verificationInput}
                onChange={(e) => setVerificationInput(e.target.value.replace(/\D/g, ''))}
                className="w-full text-center tracking-[0.5em] text-lg font-bold rounded-xl border border-[#2D3139] bg-[#0A0B0E] py-4 text-slate-200 placeholder-slate-700 transition-all focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/15"
              />
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setCodeSent(false)}
                className="flex-1 flex items-center justify-center gap-1.5 rounded-xl border border-[#2D3139] bg-[#14161C] py-3 text-xs font-bold text-slate-400 transition-all hover:bg-[#1C1F27] hover:text-white"
              >
                Retour
              </button>
              <button
                type="submit"
                disabled={actionLoading}
                className="flex-[2] flex items-center justify-center gap-2 rounded-xl bg-indigo-600 py-3 text-xs font-bold uppercase tracking-wider text-white transition-all hover:bg-indigo-500 focus:outline-none disabled:opacity-50 active:scale-[0.98]"
              >
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmer mon Email"}
              </button>
            </div>
          </form>
        )}

        <div className="mt-8 border-t border-[#2D3139]/50 pt-6 flex justify-between items-center text-[10px]">
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 text-slate-500 hover:text-red-400 transition-colors focus:outline-none"
          >
            <LogOut className="h-3.5 w-3.5" />
            Déconnexion
          </button>
          <span className="text-slate-600">🔒 Accès Sécurisé Bip Teams</span>
        </div>

      </div>
    </div>
  );
}
